/* Файлы итога заказа для хвоста движка (engine-kit, план M3 §2 S4, D5): кадры фото и любые файлы итога (видео, текст) — только через
   owner-protected маршрут заказа /app/orders/{id}/result/files/{index}?revision= (ссылки поставщика не используются), с пределами,
   которые даёт экран: сколько файлов, какие типы, размер файла, срок. Те же правила, что у родных хуков (use-photo-gallery.ts —
   фото до 30 МБ за 30 с, shoot/kit/use-order-images.ts — кадры за 60 с, slides/use-product-result.ts — до 40 файлов по 200 МБ за 120 с),
   родные хуки остаются как есть. Неудача — исключение, а созданные адреса отзываются; удачный набор отзывает его хозяин
   (createResultGallery — один набор на id:revision, release() при смене кадра или заказа). sections() — раскладка итога по секциям
   ответа сервера; archive() — архив или файл секции через endpoints.history.download (тот же owner-protected путь). */
import { endpoints } from '../../../api';
import type { SpeechOrder } from '../../../api/speech';
import { record, rows, text } from '../studio-content';

export interface GalleryLimits {
  /** Сколько файлов может быть в итоге (больше — набор не грузится) */
  limit: number;
  /** Какие файлы итога берутся (по типу ассета и по типу пришедшего файла); без него — любые */
  mimes?: RegExp;
  /** Предел одного файла, байт */
  maxBytes: number;
  /** Срок загрузки всего набора, мс */
  timeoutMs: number;
}
const MB = 1024 * 1024;
const IMAGES = /^image\/(jpeg|png|webp)$/;
/** Варианты «Фото» (use-photo-gallery.ts) */
export const PHOTO_LIMITS: GalleryLimits = { limit: 48, mimes: IMAGES, maxBytes: 30 * MB, timeoutMs: 30000 };
/** Кадры фотосессий и наборов (shoot/kit/use-order-images.ts) */
export const ORDER_IMAGE_LIMITS: GalleryLimits = { limit: 36, mimes: IMAGES, maxBytes: 30 * MB, timeoutMs: 60000 };
/** Все файлы итога товарных экранов (slides/use-product-result.ts) */
export const PRODUCT_LIMITS: GalleryLimits = { limit: 40, maxBytes: 200 * MB, timeoutMs: 120000 };

export interface GalleryItem {
  index: number;
  url: string;
  name: string;
  mime: string;
  blob: Blob;
  /** Текст файла text/* */
  text?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  hasAudio?: boolean;
}
export interface ResultSection {
  kind: string;
  title: string;
  indices: number[];
  downloadUrl: string;
}

const int = (value: unknown) => (Number.isSafeInteger(value) ? Number(value) : undefined);
/** Ассеты итога без догадок: чего нет в ответе — того нет */
function assetsOf(order: SpeechOrder | null | undefined) {
  return rows(record(order?.result).assets)
    .map(asset => ({
      index: int(asset.index) ?? -1,
      mime: text(asset.mime),
      name: text(asset.file_name),
      width: int(asset.width),
      height: int(asset.height),
      durationMs: int(asset.duration_ms),
      hasAudio: typeof asset.has_audio === 'boolean' ? asset.has_audio : undefined,
    }))
    .filter(asset => asset.index >= 0 && !!asset.mime);
}
/** Ключ набора: готовый заказ с доступным результатом и его ревизия; иначе '' */
export function galleryKey(order: SpeechOrder | null | undefined): string {
  return order?.status === 'done' && order.result?.available ? `${order.id}:${order.result_revision}` : '';
}

/** Загрузить файлы итога по пределам экрана. Бросает: результат не готов, файлов нет или больше limit, файл не того типа, пустой или больше
    maxBytes, срок вышел, signal прерван. Созданные адреса при неудаче отзываются; при удаче их отзывает тот, кто взял набор */
export async function gallery(order: SpeechOrder, limits: GalleryLimits, signal?: AbortSignal): Promise<GalleryItem[]> {
  if (!galleryKey(order)) throw new Error('result_unavailable');
  const assets = assetsOf(order).filter(asset => !limits.mimes || limits.mimes.test(asset.mime));
  if (!assets.length || assets.length > limits.limit) throw new Error('result_invalid');
  const request = new AbortController();
  const onAbort = () => request.abort();
  if (signal?.aborted) request.abort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => request.abort(), limits.timeoutMs);
  const items: GalleryItem[] = [];
  try {
    for (const asset of assets) {
      const blob = await endpoints.speech.result(order, asset.index, request.signal);
      if (request.signal.aborted) throw new Error('result_aborted');
      if (!blob.size || blob.size > limits.maxBytes || (limits.mimes && !limits.mimes.test(blob.type))) throw new Error('result_invalid');
      const content = asset.mime.startsWith('text/') ? await blob.text() : undefined;
      if (request.signal.aborted) throw new Error('result_aborted');
      const item: GalleryItem = { ...asset, url: URL.createObjectURL(blob), mime: limits.mimes ? blob.type : asset.mime, blob };
      if (content !== undefined) item.text = content;
      items.push(item);
    }
    return items;
  } catch (failure) {
    items.forEach(item => URL.revokeObjectURL(item.url));
    throw failure;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}

/** Секции итога (result.sections): только известные индексы файлов; секция без файлов не показывается */
export function sections(order: SpeechOrder | null | undefined): ResultSection[] {
  const known = new Set(assetsOf(order).map(asset => asset.index));
  return rows(record(order?.result).sections)
    .map(section => ({
      kind: text(section.kind),
      title: text(section.title) || text(section.name),
      indices: (Array.isArray(section.asset_indices) ? section.asset_indices : []).filter(
        (value): value is number => Number.isSafeInteger(value) && known.has(value as number),
      ),
      downloadUrl: text(section.download_url),
    }))
    .filter(section => !!section.kind && section.indices.length > 0);
}
/** Адрес архива итога (result.archive_url) или '' */
export function archiveUrl(order: SpeechOrder | null | undefined): string {
  return text(record(order?.result).archive_url);
}
/** Архив итога или файл секции (url — download_url секции; по умолчанию archive_url): только owner-protected путь заказа
    (historyDownloadPath отвергает чужие адреса). Бросает, если адреса нет */
export async function archive(order: SpeechOrder, url = archiveUrl(order), signal?: AbortSignal): Promise<Blob> {
  if (!url) throw new Error('result_archive_unavailable');
  return endpoints.history.download(order.id, url, signal);
}

export type GalleryState = 'idle' | 'loading' | 'ready' | 'error';
export interface ResultGallery {
  /** Набор этого заказа и ревизии: один запрос на id:revision (повторный вызов — тот же промис); null — не вышло, прервано или набор уже не нужен.
      Не бросает. Новый заказ или ревизия — прежний набор отзывается */
  load(order: SpeechOrder | null | undefined): Promise<GalleryItem[] | null>;
  /** Файлы набора этого заказа (пока не загружены — []) */
  items(order: SpeechOrder | null | undefined): GalleryItem[];
  state(order: SpeechOrder | null | undefined): GalleryState;
  /** «Повторить»: сбой этого заказа забывается, следующий load() грузит заново */
  retry(): void;
  /** Набор больше не нужен (смена кадра, уход): запрос прерывается, адреса отзываются */
  release(): void;
}
/** Один набор файлов итога на кадр экрана */
export function createResultGallery(limits: GalleryLimits): ResultGallery {
  let key = '';
  let state: GalleryState = 'idle';
  let items: GalleryItem[] = [];
  let flight: { abort: AbortController; done: Promise<GalleryItem[] | null> } | null = null;
  function drop() {
    flight?.abort.abort();
    flight = null;
    items.forEach(item => URL.revokeObjectURL(item.url));
    items = [];
    key = '';
    state = 'idle';
  }
  const mine = (order: SpeechOrder | null | undefined) => {
    const at = galleryKey(order);
    return !!at && at === key;
  };
  return {
    load(order) {
      const at = galleryKey(order);
      if (!at || !order) return Promise.resolve(null);
      if (at !== key) drop();
      if (state === 'ready') return Promise.resolve(items);
      if (state === 'error') return Promise.resolve(null);
      if (flight) return flight.done;
      key = at;
      state = 'loading';
      const abort = new AbortController();
      const done = gallery(order, limits, abort.signal).then(
        loaded => {
          if (abort.signal.aborted || key !== at) {
            loaded.forEach(item => URL.revokeObjectURL(item.url));
            return null;
          }
          items = loaded;
          state = 'ready';
          return loaded;
        },
        () => {
          if (abort.signal.aborted || key !== at) return null;
          state = 'error';
          return null;
        },
      );
      flight = { abort, done };
      void done.finally(() => {
        if (flight?.abort === abort) flight = null;
      });
      return done;
    },
    items: order => (mine(order) && state === 'ready' ? items : []),
    state: order => (mine(order) ? state : 'idle'),
    retry() {
      if (state === 'error') state = 'idle';
    },
    release: drop,
  };
}
